import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { settingsReportStyles } from "../../../styles/SettingsReport.styles";
import {
  clientNameClickableStyles,
  pickerCardStyles,
  loadingContainerStyles,
  accountsContainerStyles,
  loadingAccountsBoxStyles,
  loadingAccountsTextStyles,
  emptyAccountsTextStyles,
  accountTooltipContainerStyles,
  selectedAccountChipStyles,
  deselectedAccountChipStyles,
} from "../../../styles/ClientPicker.styles";
import {
  useListAccountsQuery,
  useGetClientQuery,
  useListClientsQuery,
  Client,
  Account,
} from "features/trading/accounts";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useReportSettings } from "./ReportSettingsContext";
import { useSettingsReportValue } from "hooks/useReportStorage";
import { PerformanceReportTypeEnum } from "helpers/types";

// Constants
const PAGINATION_CONFIG = {
  page: 0,
  pageSize: 100,
} as const;

const SETTINGS_CLIENT_ID_KEY = "client_id";
const SETTINGS_ACCOUNT_IDS_KEY = "account_ids";

const parseClientId = (value: number | string): number | undefined => {
  if (typeof value !== "string" || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseAccountIds = (value: number | string): number[] => {
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is number => typeof id === "number");
  } catch {
    return [];
  }
};

const normalizeAccountSelection = (
  accountIds: number[],
  selectedIds: number[],
): { selected: number[]; deselected: number[] } => {
  const selected = selectedIds.filter((id) => accountIds.includes(id));
  const deselected = accountIds.filter((id) => !selected.includes(id));
  return { selected, deselected };
};

// Sub-components
interface AccountTooltipProps {
  account: Account;
}

const AccountTooltip = ({ account }: AccountTooltipProps) => (
  <Box sx={accountTooltipContainerStyles}>
    <Typography variant="caption" fontWeight="bold" display="block">
      Account Details
    </Typography>
    <Typography variant="caption" display="block">
      Account Number: {account.account_number}
    </Typography>
    {account.orion_account_id && (
      <Typography variant="caption" display="block">
        Orion ID: {account.orion_account_id}
      </Typography>
    )}
    {account.custodian__name && (
      <Typography variant="caption" display="block">
        Custodian: {account.custodian__name}
      </Typography>
    )}
    {account.reg_type__name && (
      <Typography variant="caption" display="block">
        Reg Type: {account.reg_type__name}
      </Typography>
    )}
    {account.as_of_date && (
      <Typography variant="caption" display="block">
        As of Date: {new Date(account.as_of_date).toLocaleDateString()}
      </Typography>
    )}
  </Box>
);

interface AccountChipProps {
  account: Account;
  isDeselected: boolean;
  onRemove: (accountId: number) => void;
  onReselect: (accountId: number) => void;
}

const AccountChip = ({
  account,
  isDeselected,
  onRemove,
  onReselect,
}: AccountChipProps) => (
  <Tooltip title={<AccountTooltip account={account} />} arrow placement="top">
    <Chip
      label={account.account_number}
      onDelete={isDeselected ? undefined : () => onRemove(account.id)}
      onClick={isDeselected ? () => onReselect(account.id) : undefined}
      size="small"
      sx={
        isDeselected ? deselectedAccountChipStyles : selectedAccountChipStyles
      }
    />
  </Tooltip>
);

interface ClientPickerCardProps {
  isOpen: boolean;
  isLoading: boolean;
  clients: Client[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSelect: (client: Client | null) => void;
}

const ClientPickerCard = ({
  isOpen,
  isLoading,
  clients,
  searchTerm,
  onSearchChange,
  onSelect,
}: ClientPickerCardProps) => {
  if (!isOpen) return null;

  return (
    <Card sx={pickerCardStyles}>
      <CardContent>
        {isLoading ? (
          <Box sx={loadingContainerStyles}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            options={clients}
            getOptionLabel={(option) => option.name}
            loading={isLoading}
            onInputChange={(_, newValue) => onSearchChange(newValue)}
            onChange={(_, value) => onSelect(value)}
            inputValue={searchTerm}
            open
            ListboxProps={{
              sx: {
                maxHeight: 200,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search clients"
                variant="outlined"
                fullWidth
                placeholder="Type to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Typography>{option.name}</Typography>
              </Box>
            )}
            sx={{ width: "100%" }}
            noOptionsText={
              searchTerm ? "No clients found" : "Start typing to search clients"
            }
            autoFocus
          />
        )}
      </CardContent>
    </Card>
  );
};

interface AccountsListProps {
  isLoading: boolean;
  isFetching: boolean;
  accounts: Account[];
  selectedAccountIds: number[];
  deselectedAccountIds: number[];
  hasSelectedClient: boolean;
  onRemoveAccount: (accountId: number) => void;
  onReselectAccount: (accountId: number) => void;
}

const AccountsList = ({
  isLoading,
  isFetching,
  accounts,
  selectedAccountIds,
  deselectedAccountIds,
  hasSelectedClient,
  onRemoveAccount,
  onReselectAccount,
}: AccountsListProps) => {
  const selectedAccounts = useMemo(
    () => accounts.filter((account) => selectedAccountIds.includes(account.id)),
    [accounts, selectedAccountIds],
  );

  const deselectedAccounts = useMemo(
    () =>
      accounts.filter((account) => deselectedAccountIds.includes(account.id)),
    [accounts, deselectedAccountIds],
  );

  const allAccounts = useMemo(
    () => [...selectedAccounts, ...deselectedAccounts],
    [selectedAccounts, deselectedAccounts],
  );

  const isWaitingForData =
    isLoading || isFetching || (!accounts.length && hasSelectedClient);

  if (isWaitingForData) {
    return (
      <Box sx={loadingAccountsBoxStyles}>
        <CircularProgress size={16} />
        <Typography
          component="span"
          variant="body2"
          sx={loadingAccountsTextStyles}
        >
          Loading accounts...
        </Typography>
      </Box>
    );
  }

  if (allAccounts.length === 0) {
    return (
      <Typography component="span" sx={emptyAccountsTextStyles}>
        [Account Names]
      </Typography>
    );
  }

  return (
    <>
      {allAccounts.map((account) => {
        const isDeselected = deselectedAccountIds.includes(account.id);
        return (
          <AccountChip
            key={account.id}
            account={account}
            isDeselected={isDeselected}
            onRemove={onRemoveAccount}
            onReselect={onReselectAccount}
          />
        );
      })}
    </>
  );
};

interface ClientPickerProps {
  type: PerformanceReportTypeEnum;
}

const ClientPicker = ({ type }: ClientPickerProps) => {
  const settingsClientIdKey = `${SETTINGS_CLIENT_ID_KEY}_${type}`;
  const settingsAccountIdsKey = `${SETTINGS_ACCOUNT_IDS_KEY}_${type}`;
  const [storedClientId, setStoredClientId] = useSettingsReportValue(
    settingsClientIdKey,
    "",
  );
  const [storedAccountIds, setStoredAccountIds] = useSettingsReportValue(
    settingsAccountIdsKey,
    "[]",
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(
    () => parseClientId(storedClientId),
  );
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>(() =>
    parseAccountIds(storedAccountIds),
  );
  const [deselectedAccountIds, setDeselectedAccountIds] = useState<number[]>(
    [],
  );
  const { updateSettings } = useReportSettings();

  const { data: clientsData, isLoading: isClientsLoading } =
    useListClientsQuery({
      pagination: PAGINATION_CONFIG,
      filters: { search: clientSearchTerm.trim() },
    });

  const { data: selectedClient } = useGetClientQuery(selectedClientId, {
    skip: !selectedClientId,
  });

  const {
    data: accountsData,
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
  } = useListAccountsQuery(
    {
      pagination: PAGINATION_CONFIG,
      filters: { client: selectedClientId },
    },
    { skip: !selectedClientId },
  );

  const previousAccountsDataRef = useRef<Account[] | undefined>(undefined);
  const previousClientIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    setSelectedClientId(parseClientId(storedClientId));
  }, [storedClientId]);

  useEffect(() => {
    setSelectedAccountIds(parseAccountIds(storedAccountIds));
  }, [storedAccountIds]);

  useEffect(() => {
    setStoredClientId(
      selectedClientId === undefined ? "" : String(selectedClientId),
    );
  }, [selectedClientId, setStoredClientId]);

  useEffect(() => {
    setStoredAccountIds(JSON.stringify(selectedAccountIds));
  }, [selectedAccountIds, setStoredAccountIds]);

  useEffect(() => {
    const accountsChanged =
      previousAccountsDataRef.current !== accountsData?.results;
    const clientChanged = previousClientIdRef.current !== selectedClientId;

    if (accountsData?.results && accountsData.results.length > 0) {
      const accountIds = accountsData.results.map((acc) => acc.id);
      const normalized = normalizeAccountSelection(accountIds, selectedAccountIds);

      if (accountsChanged || clientChanged) {
        setSelectedAccountIds(normalized.selected);
        setDeselectedAccountIds(normalized.deselected);
        previousAccountsDataRef.current = accountsData.results;
        previousClientIdRef.current = selectedClientId;
      }
    } else if (!selectedClientId) {
      setSelectedAccountIds([]);
      setDeselectedAccountIds([]);
      previousAccountsDataRef.current = undefined;
      previousClientIdRef.current = undefined;
    }
  }, [accountsData?.results, selectedClientId, selectedAccountIds]);

  const handleClientSelect = useCallback((client: Client | null) => {
    if (client) {
      setSelectedClientId(client.id);
      setIsPickerOpen(false);
      setClientSearchTerm("");
      setSelectedAccountIds([]);
      setDeselectedAccountIds([]);
    }
  }, []);

  const handleClientClick = useCallback(() => {
    setIsPickerOpen((prev) => {
      if (prev) setClientSearchTerm("");
      return !prev;
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsPickerOpen(false);
    setClientSearchTerm("");
  }, []);

  const handleRemoveAccount = useCallback((accountId: number) => {
    setSelectedAccountIds((prev) => prev.filter((id) => id !== accountId));
    setDeselectedAccountIds((prev) => {
      if (!prev.includes(accountId)) {
        return [...prev, accountId];
      }
      return prev;
    });
  }, []);

  const handleReselectAccount = useCallback((accountId: number) => {
    setDeselectedAccountIds((prev) => prev.filter((id) => id !== accountId));
    setSelectedAccountIds((prev) => {
      if (!prev.includes(accountId)) {
        return [...prev, accountId];
      }
      return prev;
    });
  }, []);

  const selectedClientName = selectedClient?.name || "[Client Name]";

  const selectedAccountsAccountNumbers = useMemo(() => {
    const accounts = accountsData?.results ?? [];
    return selectedAccountIds
      .map((id) => accounts.find((a) => a.id === id)?.account_number)
      .filter(Boolean) as string[];
  }, [accountsData?.results, selectedAccountIds]);

  const selectedAccounts = useMemo(() => {
    const accounts = accountsData?.results ?? [];
    return selectedAccountIds
      .map((id) => accounts.find((a) => a.id === id))
      .filter(Boolean) as Account[];
  }, [accountsData?.results, selectedAccountIds]);

  const primaryOrionAccountId = selectedAccounts[0]?.orion_account_id ?? null;

  useEffect(() => {
    updateSettings({
      client_name: selectedClient?.name ?? "",
      account: selectedAccountsAccountNumbers.length
        ? selectedAccountsAccountNumbers.join(", ")
        : "",
      orion_account_id: primaryOrionAccountId,
    });
  }, [
    selectedClient?.name,
    selectedAccountsAccountNumbers,
    primaryOrionAccountId,
    updateSettings,
  ]);

  return (
    <Box>
      {/* Client */}
      <Box sx={settingsReportStyles.summaryItem}>
        <Typography component="span" sx={settingsReportStyles.label}>
          Client:
        </Typography>
        <Typography
          component="span"
          sx={clientNameClickableStyles}
          onClick={handleClientClick}
        >
          {selectedClientName}
        </Typography>
      </Box>

      {/* Client Picker Card */}
      <ClientPickerCard
        isOpen={isPickerOpen}
        isLoading={isClientsLoading}
        clients={clientsData?.results || []}
        searchTerm={clientSearchTerm}
        onSearchChange={setClientSearchTerm}
        onSelect={handleClientSelect}
      />

      {/* Accounts */}
      <Box sx={settingsReportStyles.summaryItem}>
        <Typography component="span" sx={settingsReportStyles.label}>
          Accounts:
        </Typography>
        <Box component="span" sx={accountsContainerStyles}>
          <AccountsList
            isLoading={isAccountsLoading}
            isFetching={isAccountsFetching}
            accounts={accountsData?.results || []}
            selectedAccountIds={selectedAccountIds}
            deselectedAccountIds={deselectedAccountIds}
            hasSelectedClient={!!selectedClientId}
            onRemoveAccount={handleRemoveAccount}
            onReselectAccount={handleReselectAccount}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ClientPicker;
